import { createContext, FC, useCallback, useContext, useState } from 'react';
import { CommonPopupProps, Popup } from '@silverfort/sf-components';

type PopupProps = CommonPopupProps<unknown, unknown, unknown>;

export type PopupPropsToSet<TContentProps, TFooterProps, TStateProps> = Omit<
    CommonPopupProps<TContentProps, TFooterProps, TStateProps>,
    'open' | 'onClose'
>;

interface PopupContextValue {
    openPopup: <TContentProps, TFooterProps, TStateProps>(
        popupProps: PopupPropsToSet<TContentProps, TFooterProps, TStateProps>
    ) => void;
    closePopup: () => void;
    updatePopup: <TContentProps, TFooterProps, TStateProps>(
        popupProps: Partial<PopupPropsToSet<TContentProps, TFooterProps, TStateProps>>
    ) => void;
}

const DEFAULT_POPUP_PROPS: Omit<PopupProps, 'onClose'> = {
    open: false,
    title: '',
    ContentComponent: () => null,
    FooterComponent: () => null
};

const PopupContext = createContext<PopupContextValue | undefined>(undefined);

export const PopupProvider: FC = ({ children }) => {
    const [{ popupClassName, ...popupProps }, setPopupProps] =
        useState<Omit<PopupProps, 'onClose'>>(DEFAULT_POPUP_PROPS);

    const openPopup: PopupContextValue['openPopup'] = useCallback(
        <TContentProps, TFooterProps, TStateProps>(
            props: PopupPropsToSet<TContentProps, TFooterProps, TStateProps>
        ) => {
            setPopupProps({
                ...(props as Omit<PopupProps, 'open' | 'onClose'>),
                open: true
            });
        },
        []
    );

    const closePopup: PopupContextValue['closePopup'] = useCallback(() => {
        setPopupProps((prevState) => ({ ...prevState, open: false }));
    }, []);

    const updatePopup: PopupContextValue['updatePopup'] = useCallback((props) => {
        setPopupProps((prevProps) => ({
            ...prevProps,
            ...(props as Omit<PopupProps, 'open' | 'onClose'>)
        }));
    }, []);

    const contextValue: PopupContextValue = { openPopup, closePopup, updatePopup };

    return (
        <PopupContext.Provider value={contextValue}>
            {children}
            <Popup
                {...popupProps}
                popupClassName={`transition-[width] ${popupClassName}`}
                onClose={closePopup}
                disablePortal={false}
                sx={{
                    zIndex: 5001 // TODO: Temporary override to ensure popup appears above the drawer (zIndex 5000). Remove once drawer z-index is fixed in sf-component.
                }}
            />
        </PopupContext.Provider>
    );
};

export const usePopup = () => {
    const context = useContext(PopupContext);

    if (!context) {
        throw new Error('usePopup must be used within a PopupProvider');
    }

    return context;
};
